// Dynamic combo route: /translate/$language/$location
// 18 languages × ~180 locations ≈ 3,240 long-tail landing pages with
// LegalService + Service + FAQ + HowTo + Breadcrumb JSON-LD per page.
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import {
  TRANSLATION_LANGUAGES,
  getTranslationLanguage,
} from "@/data/translation-languages";
import { getLocation, locations } from "@/data/locations";
import { TRANSLATION_DOCTYPES } from "@/data/translation-doctypes";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { FaqAccordion, ContactCTA } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { RelatedComparisons } from "@/components/RelatedComparisons";
import { RelatedServicesInLocation } from "@/components/RelatedServicesInLocation";
import { TRIPLE_KEY_LOCATIONS } from "./translate.$language.$doctype.in.$location";
const TRIPLE_KEY_HUBS = new Set<string>(TRIPLE_KEY_LOCATIONS);
import {
  Languages, MapPin, FileCheck2, ShieldCheck, BadgeCheck, Clock,
  Phone, MessageCircle, ArrowRight, Globe2,
} from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/translate/$language/in/$location")({
  loader: ({ params }) => {
    const lang = getTranslationLanguage(params.language);
    const loc = getLocation(params.location);
    if (!lang || !loc) throw notFound();
    return { langSlug: params.language, locSlug: params.location };
  },
  head: ({ params }) => {
    const l = getTranslationLanguage(params.language);
    const loc = getLocation(params.location);
    if (!l || !loc) return { meta: [{ title: ctaTitle("ไม่พบหน้า", "/translate/$language/in/$location", "th")}] };
    const url = `${SITE}/translate/${l.slug}/${loc.slug}`;
    const title = `แปล${l.nameTh} ${loc.nameTh} รับรอง ใช้ยื่นได้จริง ฿${l.priceFromTh.toLocaleString()}/หน้า | NYC`;
    const desc = `ศูนย์แปล${l.nameTh}สำหรับลูกค้า${loc.nameTh} — นักแปลขึ้นทะเบียน MoJ/NAATI · ใช้ยื่นสถานทูต กงสุล MFA · เสร็จ ${l.turnaround} · รับ-ส่งเอกสารถึงพื้นที่ — ทักไลน์เพื่อรับเช็กลิสต์เอกสารทันที`;
    const faqs = [
      {
        q: `แปล${l.nameTh}ที่${loc.nameTh} ราคาเริ่มเท่าไหร่?`,
        a: `เริ่ม ฿${l.priceFromTh.toLocaleString()}/หน้า A4 (≤250 คำ) — รับรองโดยนักแปลขึ้นทะเบียน + ตราประทับ NYC ใช้ยื่นสถานทูต กงสุล MFA และศาลได้ทันที สำหรับลูกค้า${loc.nameTh} มีบริการรับ-ส่งเอกสาร เร็วสุด ${l.turnaround}`,
      },
      {
        q: `ลูกค้า${loc.nameTh}ส่งเอกสารยังไง?`,
        a: `3 ทางเลือก — (1) ส่งสแกน/ถ่ายรูปผ่าน LINE @nycli ได้ใบเสนอราคาใน 15 นาที (2) Walk-in สำนักงานใหญ่ลาดพร้าว 95 หรือสาขาขอนแก่น/อุดรธานี/หนองคาย (3) บริการรับ-ส่งเอกสาร EMS/Messenger ทั่วประเทศ`,
      },
      ...l.faqs.slice(0, 2),
    ];

    const ratingKey = `translate/${l.slug}/${loc.slug}`;
    const rating = aggregateRatingNode(ratingKey);

    return {
      meta: [
        { title: ctaTitle(title, "/translate/$language/in/$location", "th") },
        { name: "description", content: ctaDesc(desc, "/translate/$language/in/$location", "th")},
        { name: "keywords", content: [...l.keywords, loc.nameTh, loc.nameEn, `แปล${l.nameTh} ${loc.nameTh}`].join(", ") },
        { property: "og:title", content: ctaTitle(title, "/translate/$language/in/$location", "th")},
        { property: "og:description", content: ctaDesc(desc, "/translate/$language/in/$location", "th")},
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "th_TH" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: ctaTitle(title, "/translate/$language/in/$location", "th")},
        { name: "twitter:description", content: desc },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/translate/${l.slug}/in/${loc.slug}`, `/en/translate/${l.slug}/in/${loc.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `แปล${l.nameTh} — บริการสำหรับลูกค้า${loc.nameTh}`,
              serviceType: "Certified document translation",
              areaServed: {
                "@type": "Place",
                name: loc.nameTh,
                ...(loc.lat && loc.lng
                  ? { geo: { "@type": "GeoCoordinates", latitude: loc.lat, longitude: loc.lng } }
                  : {}),
              },
              availableLanguage: [
                { "@type": "Language", name: l.nameEn, alternateName: l.nativeName, iso6391: l.iso639 },
                { "@type": "Language", name: "Thai", iso6391: "th" },
              ],
              provider: {
                "@type": "LegalService",
                "@id": `${SITE}/#org`,
                name: "NYC Legal & Notary Service",
                url: SITE,
                telephone: "+66832494999",
              },
              offers: {
                "@type": "Offer",
                price: l.priceFromTh,
                priceCurrency: "THB",
                priceSpecification: {
                  "@type": "UnitPriceSpecification",
                  price: l.priceFromTh,
                  priceCurrency: "THB",
                  unitText: "page",
                },
                availability: "https://schema.org/InStock",
              },
              aggregateRating: rating,
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "LegalService",
              "@id": `${url}#localbusiness`,
              name: `NYC Legal & Notary Service — แปล${l.nameTh} (${loc.nameTh})`,
              url,
              telephone: "+66832494999",
              priceRange: "฿฿",
              address: {
                "@type": "PostalAddress",
                streetAddress: loc.address ?? "61 ซอยลาดพร้าว 95",
                addressLocality: loc.nameTh,
                postalCode: loc.postalCode ?? "10310",
                addressCountry: "TH",
              },
              areaServed: { "@type": "Place", name: loc.nameTh },
              openingHours: "Mo-Sa 09:00-18:00",
              aggregateRating: rating,
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "แปลรายภาษา", item: `${SITE}/translate` },
                { "@type": "ListItem", position: 3, name: `แปล${l.nameTh}`, item: `${SITE}/translate/${l.slug}` },
                { "@type": "ListItem", position: 4, name: loc.nameTh, item: url },
              ],
            }),
          },
          ...reviewNodes(ratingKey, `${url}#service`, 2).map((r) => ({
            type: "application/ld+json" as const,
            children: JSON.stringify(r),
          })),
        ]),
      ],
    };
  },
  component: TranslateLangLocationPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบหน้า</h1>
      <Link to="/translate" className="mt-4 inline-block text-gold underline">
        กลับไปแปลรายภาษา
      </Link>
    </main>
  ),
});

function TranslateLangLocationPage() {
  const { langSlug, locSlug } = Route.useLoaderData() as { langSlug: string; locSlug: string };
  const l = getTranslationLanguage(langSlug)!;
  const loc = getLocation(locSlug)!;
  const others = TRANSLATION_LANGUAGES.filter((x) => x.slug !== langSlug).slice(0, 8);
  const nearbyLocations = locations
    .filter((x) => x.slug !== locSlug && x.region === loc.region)
    .slice(0, 8);
  const popularDocs = TRANSLATION_DOCTYPES.slice(0, 9);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/translate" className="hover:text-gold">แปลเอกสาร</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/translate/$language" params={{ language: l.slug }} className="hover:text-gold">{l.nameTh}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{loc.nameTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Languages className="h-3.5 w-3.5" /> Certified · {l.nameEn} · {loc.nameEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            แปล{l.nameTh} สำหรับลูกค้า{loc.nameTh} · รับรองใช้ยื่นได้ทันที
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            NYC Legal & Notary Service — ทีมนักแปล{l.nameTh}ขึ้นทะเบียน{l.isCertified ? " MoJ" : ""}{l.isNaati ? " + NAATI" : ""} พร้อมบริการรับ-ส่งเอกสารถึง{loc.nameTh} ราคาเริ่ม ฿{l.priceFromTh.toLocaleString()}/หน้า เสร็จ {l.turnaround}
          </p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "ราคาเริ่ม", v: `฿${l.priceFromTh.toLocaleString()}/หน้า` },
              { k: "เสร็จภายใน", v: l.turnaround },
              { k: "พื้นที่บริการ", v: loc.nameTh },
              { k: "Rating", v: "2,147+ เคสสำเร็จ" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-emerald-400">
              <MessageCircle className="h-4 w-4" /> ส่งไฟล์ผ่าน LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md transition hover:-translate-y-0.5">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC Legal & Notary Service (DBD 0435567000061) ให้บริการแปลเอกสาร{l.nameTh}สำหรับลูกค้า{loc.nameTh} — รับรองโดยนักแปลขึ้นทะเบียน ราคาเริ่ม ฿{l.priceFromTh.toLocaleString()}/หน้า เสร็จภายใน {l.turnaround} ใช้ยื่นสถานทูต กงสุล กระทรวงการต่างประเทศ ศาลได้ทันที พร้อมบริการรับ-ส่งเอกสารถึงพื้นที่{loc.nameTh}
        </blockquote>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <MapPin className="inline h-6 w-6 text-amber-600" /> บริการแปล{l.nameTh}ใน{loc.nameTh}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-700 sm:text-base">
            ลูกค้าใน{loc.nameTh}สามารถใช้บริการแปล{l.nameTh}ของ NYC ได้ 3 ช่องทาง — ส่งสแกนผ่าน LINE @nycli (รับใบเสนอราคาใน 15 นาที), Walk-in ที่สำนักงาน{loc.region === "กรุงเทพมหานคร" ? "ใหญ่ลาดพร้าว 95" : "สาขาที่ใกล้ที่สุด"}, หรือใช้บริการรับ-ส่งเอกสาร EMS/Messenger ทั่วประเทศ {loc.description ? `· ${loc.description}` : ""}
          </p>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">เอกสาร{l.nameTh}ที่แปลบ่อย</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {l.commonDocs.map((doc) => (
              <li key={doc} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {doc}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ประเทศปลายทาง</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {l.countryOfUse.map((c) => (
              <span key={c} className="inline-flex items-center gap-1.5 rounded-full border border-navy-deep/15 bg-white px-3.5 py-1.5 text-[13px] font-semibold text-navy-deep">
                <Globe2 className="h-3.5 w-3.5 text-amber-600" /> {c}
              </span>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">ราคาเริ่มต้น · {loc.nameTh}</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            แปล{l.nameTh} ฿{l.priceFromTh.toLocaleString()}/หน้า
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ตราประทับ + ลายเซ็นนักแปลขึ้นทะเบียน</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> เสร็จภายใน {l.turnaround}</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> รับ-ส่ง EMS ทั่ว{loc.region}</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Notary + MFA + สถานทูตครบในที่เดียว</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-2 sm:p-3">
            <FaqAccordion items={[
              {
                q: `แปล${l.nameTh}ที่${loc.nameTh} ราคาเริ่มเท่าไหร่?`,
                a: `เริ่ม ฿${l.priceFromTh.toLocaleString()}/หน้า — รับรองโดยนักแปลขึ้นทะเบียน ใช้ยื่นสถานทูต/กงสุล/MFA ได้ เสร็จ ${l.turnaround}`,
              },
              {
                q: `ลูกค้า${loc.nameTh}ส่งเอกสารยังไง?`,
                a: `ส่งสแกนผ่าน LINE @nycli (ใบเสนอราคาใน 15 นาที), Walk-in สำนักงาน, หรือใช้ EMS/Messenger รับ-ส่งทั่วประเทศ`,
              },
              ...l.faqs.slice(0, 2),
            ]} />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            เลือกประเภทเอกสาร → แปล{l.nameTh} ที่{loc.nameTh}
          </h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {popularDocs.map((d) => {
              const tripleEligible = TRIPLE_KEY_HUBS.has(locSlug);
              return tripleEligible ? (
                <Link
                  key={d.slug}
                  to="/translate/$language/$doctype/in/$location"
                  params={{ language: l.slug, doctype: d.slug, location: locSlug }}
                  className="group rounded-2xl border border-amber-200 bg-amber-50/30 p-4 transition hover:-translate-y-0.5 hover:border-gold/60 hover:shadow-md"
                >
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{d.nameEn} · {loc.nameEn}</div>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">แปล{d.nameTh} ที่{loc.nameTh}</div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-amber-600" />
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">เริ่ม ฿{(l.priceFromTh + d.surcharge).toLocaleString()}</div>
                </Link>
              ) : (
                <Link
                  key={d.slug}
                  to="/translate/$language/$doctype"
                  params={{ language: l.slug, doctype: d.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
                >
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{d.nameEn}</div>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">แปล{d.nameTh}</div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-amber-600" />
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">เริ่ม ฿{(l.priceFromTh + d.surcharge).toLocaleString()}</div>
                </Link>
              );
            })}
          </div>
        </section>


        {nearbyLocations.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">พื้นที่ใกล้เคียง · แปล{l.nameTh}</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {nearbyLocations.map((n) => (
                <Link
                  key={n.slug}
                  to="/translate/$language/in/$location"
                  params={{ language: l.slug, location: n.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
                >
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{n.region}</div>
                  <div className="mt-1 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                    แปล{l.nameTh} {n.nameTh}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ภาษาอื่น · {loc.nameTh}</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((o) => (
              <Link
                key={o.slug}
                to="/translate/$language/in/$location"
                params={{ language: o.slug, location: loc.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{o.nameEn}</div>
                <div className="mt-1 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                  แปล{o.nameTh} {loc.nameTh}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">เริ่ม ฿{o.priceFromTh.toLocaleString()}</div>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <RelatedServicesInLocation currentService="translate" locationSlug={locSlug} locale="th" />
      <RelatedComparisons category="translate" locale="th" />
      <TrustLayer variant="compact" />
    </main>
  );
}
